import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Receipt, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { api, formatIDR, formatApiError } from "../lib/api";
import { SkeletonCard } from "../components/Skeleton";
import { Button } from "../components/ui/button";

const TYPE_LABELS = {
    pulsa: "Pulsa", data: "Paket Data", pln: "PLN", other: "Lainnya", pln_token: "Token PLN", pln_bill: "Tagihan PLN",
    bpjs: "BPJS", game: "Game", ewallet: "E-Wallet", pdam: "PDAM", transfer: "Transfer",
};

export default function StatsPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setError("");
        api.get("/users/stats", { signal: controller.signal }).then(({ data }) => setStats(data)).catch((err) => { if (!controller.signal.aborted) setError(formatApiError(err)); });
        return () => controller.abort();
    }, [revision]);

    const dailyData = stats
        ? Object.entries(stats.daily || {}).sort().map(([d, v]) => ({
            date: new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
            profit: Number(v),
        }))
        : [];

    const byTypeData = stats
        ? Object.entries(stats.by_type || {}).map(([k, v]) => ({ name: TYPE_LABELS[k] || k, count: Number(v) }))
        : [];

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)} data-testid="stats-back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="font-heading font-bold text-xl tracking-tight">Statistik Penjualan</h1>
            </div>

            {error && <div role="alert" className="rounded-xl border border-destructive/30 p-4 text-sm text-destructive">{error}<Button variant="outline" className="ml-2" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button></div>}
            <div className="grid grid-cols-2 gap-3">
                {!stats ? (error ? null : <><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></>) : (
                    <>
                        <StatCard icon={<Receipt className="h-4 w-4" />} label="TX Hari Ini" value={stats.total_today_tx} />
                        <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Profit Hari Ini" value={formatIDR(stats.profit_today)} green />
                        <StatCard icon={<Receipt className="h-4 w-4" />} label="TX 7 Hari" value={stats.total_week_tx} />
                        <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Profit 7 Hari" value={formatIDR(stats.profit_week)} green />
                    </>
                )}
            </div>

            {dailyData.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-card border border-border p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="h-4 w-4 text-brand-green" />
                        <div className="font-heading font-semibold">Profit Harian (7 hari)</div>
                    </div>
                    <div className="h-44">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dailyData}>
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v / 1000}k`} />
                                <Tooltip
                                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                                    formatter={(v) => formatIDR(v)}
                                />
                                <Bar dataKey="profit" fill="#10B981" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>
            )}

            {byTypeData.length > 0 && (
                <div className="rounded-2xl bg-card border border-border p-5">
                    <div className="font-heading font-semibold mb-3">Per Kategori</div>
                    <div className="space-y-3">
                        {byTypeData.map((t) => {
                            const max = Math.max(...byTypeData.map((d) => d.count));
                            const pct = max > 0 ? (t.count / max) * 100 : 0;
                            return (
                                <div key={t.name}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium">{t.name}</span>
                                        <span className="font-heading font-bold">{t.count} tx</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }}
                                            className="h-full bg-brand-green rounded-full"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {stats && stats.total_week_tx === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">
                    Belum ada data — lakukan transaksi untuk melihat statistik.
                </div>
            )}
        </div>
    );
}

function StatCard({ icon, label, value, green }) {
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-card border border-border p-4">
            <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-brand-green">{icon}</div>
            </div>
            <div className={`font-heading font-bold text-xl mt-1 ${green ? "text-brand-green" : ""}`}>{value}</div>
        </motion.div>
    );
}
